import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#071426] text-white">
      <header className="border-b border-[#1F3A5C]">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <BrandMark />
          <Link to="/" className="text-sm text-[#AAB7C7] hover:text-white inline-flex items-center gap-1" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-5 py-16">
        <p className="text-xs uppercase tracking-[0.22em] text-[#C7F500] font-bold mb-3">Enquiries</p>
        <h1 className="font-heading text-4xl sm:text-5xl leading-tight">TALK TO US</h1>
        <p className="mt-3 text-[#AAB7C7] text-lg max-w-2xl">
          Running a pub league, a workplace pool, or a supporters&apos; club competition? Send a note and we&apos;ll come back to you within a day.
        </p>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            {sent ? (
              <div className="p-8 rounded-2xl bg-[#0D2038] border border-[#C7F500]/40 fade-up" data-testid="enquiry-sent">
                <CheckCircle2 className="w-10 h-10 text-[#C7F500]" />
                <h2 className="font-heading text-2xl mt-3">Message received.</h2>
                <p className="text-[#AAB7C7] mt-2">
                  Thanks {form.name.split(" ")[0] || "there"} — we&apos;ll reply to <b className="text-white">{form.email}</b> as soon as we can. Usually within 24 hours.
                </p>
                <Link to="/" className="inline-flex items-center gap-1 mt-6 text-[#C7F500] hover:underline">
                  <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 sm:p-8 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] space-y-4" data-testid="enquiry-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#AAB7C7] mb-1.5">Your name</label>
                    <input required maxLength={200} value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="enquiry-name"
                      className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#AAB7C7] mb-1.5">Email</label>
                    <input required type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="enquiry-email"
                      className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#AAB7C7] mb-1.5">Organisation <span className="text-[#7A8AA3] normal-case">(optional)</span></label>
                  <input maxLength={200} value={form.organisation}
                    onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                    data-testid="enquiry-org"
                    placeholder="Pub, workplace, supporters&apos; club…"
                    className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#AAB7C7] mb-1.5">Message</label>
                  <textarea required minLength={5} maxLength={4000} rows={6} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="enquiry-message"
                    placeholder="How many players, when you'd like to start, any questions…"
                    className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5 resize-y" />
                </div>
                {err && <div className="text-sm text-red-400" data-testid="enquiry-error">{err}</div>}
                <button type="submit" disabled={busy} data-testid="enquiry-submit"
                  className="w-full py-3 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-60 font-semibold text-[#071426] transition-colors">
                  {busy ? "Sending…" : "Send enquiry"}
                </button>
                <p className="text-[11px] text-[#7A8AA3]">
                  We only use your details to reply to this enquiry. No newsletter, no spam.
                </p>
              </form>
            )}
          </div>

          <aside className="space-y-4">
            <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="response-panel">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#C7F500]" />
                <h3 className="font-heading text-base">Fast response</h3>
              </div>
              <p className="text-sm text-[#AAB7C7]">
                Every enquiry goes straight to the founder&apos;s inbox. Most messages get a reply the same day, and never longer than 24 hours on weekdays.
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
              <h3 className="font-heading text-base mb-2">Already using it?</h3>
              <p className="text-sm text-[#AAB7C7] mb-3">Sign in and hit us up from inside the app so we know which league you&apos;re asking about.</p>
              <Link to="/login" className="text-sm text-[#C7F500] hover:underline" data-testid="enquiry-signin-link">Sign in →</Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
